import { useCallback, useEffect, useMemo, useState } from "react";
import {
  APPEARANCE_STORAGE_KEY,
  applyAppearance,
  getNextAppearance,
  isAppearancePreference,
  resolveAppearance,
  type AppearancePreference,
} from "../lib/appearance";

function getStoredAppearance(): AppearancePreference {
  try {
    const stored = localStorage.getItem(APPEARANCE_STORAGE_KEY);
    return isAppearancePreference(stored) ? stored : "system";
  } catch (error) {
    console.warn("Appearance preference could not be read.", error);
    return "system";
  }
}

export function useAppearance() {
  const media = useMemo(
    () => window.matchMedia("(prefers-color-scheme: dark)"),
    [],
  );
  const [preference, setPreference] =
    useState<AppearancePreference>(getStoredAppearance);
  const [resolvedTheme, setResolvedTheme] = useState(() =>
    resolveAppearance(preference, media.matches),
  );

  useEffect(() => {
    const updateResolvedTheme = () => {
      const nextTheme = resolveAppearance(preference, media.matches);
      setResolvedTheme(nextTheme);
      applyAppearance(preference, nextTheme);
    };

    updateResolvedTheme();
    media.addEventListener("change", updateResolvedTheme);
    return () => media.removeEventListener("change", updateResolvedTheme);
  }, [media, preference]);

  const cycleAppearance = useCallback(() => {
    setPreference((current) => {
      const next = getNextAppearance(current, resolvedTheme);
      try {
        localStorage.setItem(APPEARANCE_STORAGE_KEY, next);
      } catch (error) {
        console.warn("Appearance preference could not be saved.", error);
      }
      return next;
    });
  }, [resolvedTheme]);

  return { preference, resolvedTheme, cycleAppearance };
}
